/**
 * domains/market/engines/setup2/scoring.ts — MER Algo Footprints v7.1H
 *
 * Deterministic weighted scoring for RichSetup.
 * All functions are pure — no side effects.
 * Every scoring decision is explainable from its inputs.
 */

import {
  QualityBreakdown, ScoringWeights, Penalty, SetupClass, SetupDirection,
} from './types';
import type { SMCResult, RegimeResult, LiquidityZone, TrendResult } from '../../../../shared/types';

// ─── Structure score (25%) ────────────────────────────────────────────────────
// How clean and confirmed is the market structure for this setup?

export function scoreStructure(params: {
  smc:          SMCResult | null;
  direction:    SetupDirection;
  setupClass:   SetupClass;
  candles_len:  number;
}): number {
  const { smc, direction, setupClass, candles_len } = params;
  if (!smc) return 10;

  let score = 25;
  const smcDir = direction === 'LONG' ? 'bullish' : direction === 'SHORT' ? 'bearish' : null;

  // BOS in direction — recency-weighted
  if (smc.lastBOS && smc.lastBOS.dir === smcDir) {
    const age = candles_len - 1 - smc.lastBOS.candleIdx;
    score += age <= 3  ? 30 : age <= 8 ? 22 : age <= 15 ? 12 : 4;
  }

  // CHOCH — weighted by setup class (reversals get more credit)
  if (smc.lastCHOCH && smc.lastCHOCH.dir === smcDir) {
    const age  = candles_len - 1 - smc.lastCHOCH.candleIdx;
    const base = (setupClass === 'REVERSAL' || setupClass === 'TRAP_REVERSAL') ? 32 : 20;
    score += age <= 5 ? base : age <= 10 ? Math.round(base * 0.65) : Math.round(base * 0.35);
  }

  // Bias strength
  if (smc.bias === (direction === 'LONG' ? 'bullish' : 'bearish'))
    score += smc.biasStrength === 'major' ? 15 : 8;

  // Displacement freshness + strength
  if (smc.recentDisplacement?.dir === (direction === 'LONG' ? 'bullish' : 'bearish')) {
    const disp = smc.recentDisplacement;
    score += Math.round(disp.size * 15) + Math.round((smc.displacementFreshness ?? 0) * 5);
  }

  return Math.min(100, Math.max(0, score));
}

// ─── Context score (20%) ─────────────────────────────────────────────────────
// How well does the macro context (regime, trend, phase) support this setup?

export function scoreContext(params: {
  regime:      RegimeResult | null;
  trend:       TrendResult | null;
  direction:   SetupDirection;
  setupClass:  SetupClass;
}): number {
  const { regime, trend, direction, setupClass } = params;
  if (!regime) return 10;

  let score = 15;

  const regimeFavorable =
    (direction === 'LONG'  && (regime.posture === 'bullish' || regime.posture === 'long-side')) ||
    (direction === 'SHORT' && (regime.posture === 'bearish' || regime.posture === 'short-side')) ||
    direction === 'NEUTRAL';

  score += regimeFavorable ? 30 : -15;
  score += Math.round((regime.confidence ?? 0) * 0.25);

  if (setupClass === 'MEAN_REVERSION' &&
      (regime.dominant === 'meanReversion' || regime.dominant === 'noTrade'))
    score += 15;

  if (setupClass === 'BREAKOUT_CONFIRMATION' && regime.dominant === 'breakout')
    score += 12;

  if (trend) {
    const td = trend.trendDirection;
    const inTrend = trend.trendState === 'trend' || trend.trendState === 'pullback';
    if (direction === 'LONG'  && td === 'bullish' && inTrend) score += 20;
    if (direction === 'SHORT' && td === 'bearish' && inTrend) score += 20;
    if (direction === 'LONG'  && td === 'bearish' && inTrend) score -= 12;
    if (direction === 'SHORT' && td === 'bullish' && inTrend) score -= 12;
    if (trend.trendState === 'compression' || trend.trendState === 'transition')
      score += setupClass === 'MEAN_REVERSION' ? 5 : -5;
  }

  return Math.min(100, Math.max(0, score));
}

// ─── Liquidity score (15%) ────────────────────────────────────────────────────

export function scoreLiquidity(params: {
  smc:        SMCResult | null;
  zones:      LiquidityZone[];
  curPrice:   number;
  direction:  SetupDirection;
  setupClass: SetupClass;
}): number {
  const { smc, zones, curPrice, direction, setupClass } = params;
  let score = 15;

  // High-sig zones within 1.5% — graduated by proximity
  for (const z of zones.filter(z => z.sig === 'High')) {
    const dist = Math.abs(z.price - curPrice) / curPrice;
    if (dist < 0.005) score += 20;
    else if (dist < 0.01) score += 12;
    else if (dist < 0.015) score += 6;
  }
  score = Math.min(score, 55); // cap zone accumulation

  if (smc?.recentSweep?.reclaimed) {
    score += (setupClass === 'LIQUIDITY_SWEEP' || setupClass === 'TRAP_REVERSAL') ? 35 : 20;
  }

  // FVG quality — unfilled major
  const highQFVG = (smc?.allFVGs ?? []).filter((f: any) => f.significance === 'major' && !f.filled);
  score += highQFVG.length > 0 ? 15 : 0;

  // Premium/discount zone alignment
  const pd = smc?.premiumDiscount;
  if (pd) {
    if ((direction === 'LONG'  && pd.zone === 'discount') ||
        (direction === 'SHORT' && pd.zone === 'premium'))  score += 15;
    if ((direction === 'LONG'  && pd.zone === 'premium') ||
        (direction === 'SHORT' && pd.zone === 'discount')) score -= 10;
  }

  score += Math.round((smc?.liquiditySequenceConfidence ?? 0) * 15);

  return Math.min(100, Math.max(0, score));
}

// ─── Trigger score (15%) ─────────────────────────────────────────────────────
// Graded: absent / forming / partial / confirmed

export type TriggerConfidence = 'none' | 'forming' | 'partial' | 'confirmed';

export function scoreTrigger(params: {
  triggerConfidence: TriggerConfidence;
  triggerSpecific:   boolean;
  hasConfirmation:   boolean;
  dispFreshness:     number;   // 0–1
}): number {
  const { triggerConfidence, triggerSpecific, hasConfirmation, dispFreshness } = params;

  if (triggerConfidence === 'none') return 0;

  let score = triggerConfidence === 'confirmed' ? 45
            : triggerConfidence === 'partial'   ? 28
            :                                     14;  // 'forming'

  if (triggerSpecific) score += 20;
  if (hasConfirmation) score += 15;
  score += Math.round(dispFreshness * 10);

  return Math.min(100, Math.max(0, score));
}

// ─── Stop quality computation (internal helper for risk_reward scorer) ────────
// Determines stop quality from structural distance and clarity — replaces hardcoded 70/20

export function computeStopQuality(params: {
  stopLevel:        number | null;
  entryPrice:       number;
  smc:              SMCResult | null;
  candles_len:      number;
  stopLevelClear:   boolean;
}): number {
  const { stopLevel, entryPrice, smc, candles_len, stopLevelClear } = params;

  if (!stopLevel || !stopLevelClear) return 15;

  let score = 30;

  // Distance clarity — stop not too tight, not too wide
  const distPct = Math.abs(stopLevel - entryPrice) / entryPrice;
  if (distPct >= 0.003 && distPct <= 0.015) score += 30;  // clean window
  else if (distPct > 0.015 && distPct <= 0.03) score += 15; // wide but ok
  else if (distPct < 0.003) score += 5;                     // dangerously tight
  // > 3% stop is poor quality — no bonus

  // Structural backing — stop anchored near a meaningful event
  const bosPrice   = smc?.lastBOS?.price;
  const chochPrice = smc?.lastCHOCH?.price;
  const anchorPrice = bosPrice ?? chochPrice;
  if (anchorPrice) {
    const anchorDist = Math.abs(stopLevel - anchorPrice) / entryPrice;
    if (anchorDist < 0.005) score += 25; // tightly anchored to structural level
    else if (anchorDist < 0.01) score += 12;
    else if (anchorDist < 0.02) score += 5;
  }

  // Structural recency — older structure = less reliable stop
  const latestStructure = Math.min(
    smc?.lastBOS?.candleIdx  != null ? candles_len - 1 - smc.lastBOS.candleIdx  : Infinity,
    smc?.lastCHOCH?.candleIdx != null ? candles_len - 1 - smc.lastCHOCH.candleIdx : Infinity,
  );
  if (latestStructure <= 5)  score += 10;
  else if (latestStructure > 20) score -= 10;

  return Math.min(100, Math.max(0, score));
}

// ─── Risk/reward score (15%) ─────────────────────────────────────────────────

export function scoreRiskReward(params: {
  rr:           number | null;
  stopQuality:  number;
  stopClear:    boolean;
  target1:      number | null;
  target2:      number | null;
  entryPrice:   number;
  smc:          SMCResult | null;
  zones:        LiquidityZone[];
}): number {
  const { rr, stopQuality, stopClear, target1, target2, entryPrice, zones } = params;
  if (!stopClear) return 10;

  let score = 15;

  // R/R quality curve
  if (rr !== null) {
    score += rr >= 4.0 ? 55 : rr >= 3.0 ? 45 : rr >= 2.0 ? 32
           : rr >= 1.5 ? 20 : rr >= 1.0 ? 8 : 0;
  }

  // Stop quality contribution
  score += Math.round(stopQuality * 0.20);

  // Target structural support — is target backed by a key zone?
  if (target1 !== null) {
    const targetZone = zones.find(z =>
      Math.abs(z.price - target1) / entryPrice < 0.005 && z.sig === 'High');
    if (targetZone) score += 12;  // target at structural level

    // Target 2 adds further credibility
    if (target2 !== null) score += 5;
  }

  return Math.min(100, Math.max(0, score));
}

// ─── Execution quality score (10%) ────────────────────────────────────────────
// Note: spread is not modeled as a real input — treated as an honest assumption flag

export type SpreadAssumption = 'liquid' | 'normal' | 'unknown';

export function scoreExecutionQuality(params: {
  dispFreshness:     number;
  dispStrength:      number;
  rvol:              number;
  atKeyZone:         boolean;
  spreadAssumption:  SpreadAssumption;  // honest flag: not hardcoded true
}): number {
  const { dispFreshness, dispStrength, rvol, atKeyZone, spreadAssumption } = params;

  let score = 15;
  score += Math.round(dispFreshness * 25);
  score += Math.round(dispStrength  * 20);
  score += rvol >= 2.0 ? 25 : rvol >= 1.5 ? 18 : rvol >= 1.1 ? 10 : 0;
  if (atKeyZone) score += 15;

  // Spread assumption impact — honest treatment
  score += spreadAssumption === 'liquid'  ? 5
         : spreadAssumption === 'unknown' ? -5
         : 0;  // 'normal' = neutral

  return Math.min(100, Math.max(0, score));
}

// ─── Weighted composite ───────────────────────────────────────────────────────

export function computeQualityBreakdown(components: {
  structure:    number;
  context:      number;
  liquidity:    number;
  trigger:      number;
  risk_reward:  number;
  execution:    number;
  penalties:    Penalty[];
  weights:      ScoringWeights;
}): QualityBreakdown {
  const { structure, context, liquidity, trigger, risk_reward, execution, penalties, weights } = components;

  const raw_composite = Math.round(
    structure   * weights.structure         +
    context     * weights.context           +
    liquidity   * weights.liquidity         +
    trigger     * weights.trigger           +
    risk_reward * weights.risk_reward       +
    execution   * weights.execution_quality
  );

  const total_penalty = penalties.reduce((s, p) => s + p.points, 0);
  const setup_score   = Math.min(100, Math.max(0, raw_composite - total_penalty));

  return {
    structure_score:         Math.round(structure),
    context_score:           Math.round(context),
    liquidity_score:         Math.round(liquidity),
    trigger_score:           Math.round(trigger),
    risk_reward_score:       Math.round(risk_reward),
    execution_quality_score: Math.round(execution),
    raw_composite,
    total_penalty,
    setup_score,
  };
}
